import { unstable_useForkRef as useForkRef } from '@mui/utils'
import {
  type FC,
  type PropsWithChildren,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import { useIntersectionObserver } from 'usehooks-ts'

import { useProductPageCarouselZoomableImageContext } from '../../ProductPageCarouselZoomableImage'
import { useOptionalProductCarouselFocus } from '../ProductCarouselFocusContext'

import { type ProductCarouselGalleryItemProps } from './ProductCarouselGalleryItemProps'

const ProductCarouselGalleryItem: FC<
  PropsWithChildren<ProductCarouselGalleryItemProps>
> = ({
  'aria-label': ariaLabel,
  children,
  image,
  index,
  onClick,
  onScrolledInView,
  onTabNavigation,
  zoomEnabled = false,
}) => {
  const intl = useIntl()
  const zoomableImageContext = useProductPageCarouselZoomableImageContext()
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.95,
  })
  const focusContext = useOptionalProductCarouselFocus()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [intersectingNotificationSent, setIntersectingNotificationSent] =
    useState(false)
  const useZooming = zoomEnabled && image

  // eslint-disable-next-line react-hooks/immutability -- don't reference, refactor if touched
  useEffect(() => {
    if (focusContext) {
      // eslint-disable-next-line react-hooks/immutability -- don't reference, refactor if touched
      focusContext.itemRefs.current[index] = buttonRef.current
    }
  }, [index, focusContext])

  useEffect(() => {
    if (isIntersecting) {
      if (!intersectingNotificationSent) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
        setIntersectingNotificationSent(true)
        onScrolledInView()
      }
    } else {
      setIntersectingNotificationSent(false)
    }
  }, [intersectingNotificationSent, isIntersecting, onScrolledInView])

  return (
    <button
      aria-label={
        ariaLabel ??
        intl.formatMessage({
          id: 'product.button.label.viewLarger',
        })
      }
      className="pointer-events-none relative w-full flex-none cursor-pointer snap-center tablet:pointer-events-auto"
      data-qa-id={`product-image-${index}`}
      onClick={onClick}
      onFocus={(): void => {
        onTabNavigation?.(index)
      }}
      onPointerEnter={
        useZooming
          ? (): void => {
              zoomableImageContext.handleEnableZoomOnImageWrapperPointerEnter(
                image,
              )
            }
          : undefined
      }
      onPointerLeave={
        useZooming
          ? zoomableImageContext.handleDisableZoomOnImageWrapperPointerLeave
          : undefined
      }
      onPointerMove={
        useZooming
          ? zoomableImageContext.handleMoveZoomOnImageWrapperPointerMove
          : undefined
      }
      ref={useForkRef(ref, buttonRef)}
      type="button"
    >
      {children}
    </button>
  )
}

export { ProductCarouselGalleryItem }
